import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { useCriarProdutor } from '../api';
import { Campo, SelectBox, Segmented } from './cadastro-ui';
import { inputCls, maskCnpj, maskCpf, maskTelefone, UFS } from './cadastro-helpers';

// Tela · Cadastro de novo produtor (PF/PJ). Cria um cliente; aparece na lista de
// produtores após vincular uma fazenda. Fiel ao Figma "Cadastro · Novo produtor".
export function NovoProdutorView() {
  const navigate = useNavigate();
  const criar = useCriarProdutor();

  const [tipo, setTipo] = useState<'PF' | 'PJ'>('PF');
  const [nome, setNome] = useState('');
  const [doc, setDoc] = useState('');
  const [telefone, setTelefone] = useState('');
  const [email, setEmail] = useState('');
  const [municipio, setMunicipio] = useState('');
  const [uf, setUf] = useState('RO');
  const [erro, setErro] = useState<string | null>(null);

  const pf = tipo === 'PF';
  const docDigits = doc.replace(/\D/g, '');
  const podeSalvar = nome.trim().length > 1 && docDigits.length >= (pf ? 11 : 14) && !criar.isPending;

  async function salvar() {
    if (!podeSalvar) return;
    setErro(null);
    try {
      await criar.mutateAsync({
        cpf: doc,
        nome: nome.trim(),
        telefone: telefone.trim() || null,
        email: email.trim() || null,
        municipio: municipio.trim() || null,
        uf,
        tipo_pessoa: tipo,
      });
      // Fluxo guiado: produtor criado → cadastrar a primeira fazenda dele.
      void navigate({ to: '/agronomia/nova-fazenda', search: { cpf: docDigits } });
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível salvar o produtor.');
    }
  }

  return (
    <div className="relative flex h-full flex-col bg-bg">
      <header className="flex items-center gap-1 px-3 pt-4 pb-3 shrink-0">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="-ml-1 flex size-9 shrink-0 items-center justify-center rounded-full text-text hover:bg-surface2"
          aria-label="Voltar"
        >
          <ChevronLeft size={26} strokeWidth={2} />
        </button>
        <h1 className="text-[1.35rem] font-bold leading-tight tracking-tight text-text">Novo produtor</h1>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-40">
        <div className="flex flex-col gap-4">
          <Segmented
            value={tipo}
            onChange={setTipo}
            options={[
              { value: 'PF', label: 'Pessoa física' },
              { value: 'PJ', label: 'Pessoa jurídica' },
            ]}
          />

          <Campo label={pf ? 'Nome completo' : 'Razão social'}>
            <input
              className={inputCls}
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder={pf ? 'Ex.: João Pereira da Silva' : 'Ex.: Agropecuária Vilhena Ltda'}
              autoCapitalize="words"
            />
          </Campo>

          <Campo label={pf ? 'CPF' : 'CNPJ'}>
            <input
              className={inputCls}
              value={doc}
              onChange={(e) => setDoc(pf ? maskCpf(e.target.value) : maskCnpj(e.target.value))}
              placeholder={pf ? '000.000.000-00' : '00.000.000/0000-00'}
              inputMode="numeric"
            />
          </Campo>

          <Campo label="Telefone / WhatsApp">
            <input
              className={inputCls}
              value={telefone}
              onChange={(e) => setTelefone(maskTelefone(e.target.value))}
              placeholder="(69) 9 0000-0000"
              inputMode="tel"
            />
          </Campo>

          <Campo label="E-mail">
            <input
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@exemplo.com"
              inputMode="email"
              autoCapitalize="none"
            />
          </Campo>

          <div className="grid grid-cols-[1fr_96px] gap-3">
            <Campo label="Município">
              <input
                className={inputCls}
                value={municipio}
                onChange={(e) => setMunicipio(e.target.value)}
                placeholder="Vilhena"
                autoCapitalize="words"
              />
            </Campo>
            <Campo label="UF">
              <SelectBox value={uf} onChange={setUf} options={UFS} />
            </Campo>
          </div>

          {erro && <p className="text-[0.82rem] text-[#b3261e]">{erro}</p>}

          <button
            type="button"
            onClick={() => void salvar()}
            disabled={!podeSalvar}
            className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--accent)] py-3.5 text-[0.95rem] font-semibold text-[var(--on-solid)] disabled:opacity-50"
          >
            {criar.isPending && <Loader2 size={17} strokeWidth={2} className="animate-spin" />}
            Salvar produtor
          </button>
        </div>
      </div>
    </div>
  );
}
